import { useState } from "react";
import { api } from "../lib/api";
import { useSubscription } from "../context/SubscriptionContext";
import { Check, Sparkles, ArrowRight, X, Zap, Crown, School, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLAN_ICONS = { basic: Zap, premium: Crown, schools: School };
const PLAN_ACCENT = {
  basic: { bg: "bg-slate-50", ring: "border-slate-200", badge: null, dark: false },
  premium: { bg: "bg-[#FEF9C3]", ring: "border-[#FFD600]", badge: "NAJPOPULARNIEJSZY", dark: false },
  schools: { bg: "bg-slate-900 text-white", ring: "border-slate-900", badge: "DLA KLAS", dark: true },
};

function fmtPLN(v) {
  return v.toFixed(2).replace(".", ",");
}

export default function PricingView({ onClose, reason }) {
  const { plans, subscription } = useSubscription();
  const [loadingKey, setLoadingKey] = useState(null);
  const [interval, setInterval] = useState("year"); // default annual (highlight savings)

  const checkout = async (lookupKey) => {
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się otworzyć płatności");
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-view">
      <header className="w-full border-b border-slate-100 sticky top-0 bg-white/90 backdrop-blur z-10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Sparkles className="w-4 h-4 text-yellow-500" /> Wybierz plan
          </div>
          {onClose && (
            <button
              onClick={onClose}
              data-testid="pricing-close-button"
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"
              aria-label="Zamknij"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-8 sm:py-14 ss-fadein">
        {reason === "quota" && (
          <div className="bg-[#FEF9C3] border border-yellow-200 rounded-2xl px-5 py-4 mb-6" data-testid="quota-banner">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Świetna robota — ukończyłeś pierwszy quiz!</div>
            <p className="text-slate-900 mt-1">Wybierz plan, aby kontynuować, zobaczyć swój wynik i uczyć się dalej bez limitów.</p>
          </div>
        )}

        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
          Wybierz plan,<br />
          <span className="relative">
            aby kontynuować.
            <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#FFD600] -z-10 rounded" />
          </span>
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-lg">
          Pierwszy quiz ukończyłeś za darmo. Teraz czas odblokować wyniki i kolejne tematy — wybierz plan dopasowany do Twojego tempa nauki.
        </p>

        {/* Interval toggle */}
        <div
          className="mt-8 inline-flex items-center bg-slate-100 rounded-full p-1"
          role="tablist"
          data-testid="interval-toggle"
        >
          <button
            data-testid="interval-month"
            role="tab"
            aria-selected={interval === "month"}
            onClick={() => setInterval("month")}
            className={`text-sm font-bold px-4 py-1.5 rounded-full transition-colors ${
              interval === "month" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Miesięcznie
          </button>
          <button
            data-testid="interval-year"
            role="tab"
            aria-selected={interval === "year"}
            onClick={() => setInterval("year")}
            className={`text-sm font-bold px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
              interval === "year" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Rocznie
            <span className="text-[10px] font-bold bg-[#FFD600] text-slate-900 rounded-full px-1.5 py-0.5">
              -20%
            </span>
          </button>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Zap;
            const accent = PLAN_ACCENT[p.id] || PLAN_ACCENT.basic;
            const priceObj = interval === "year" ? p.yearly : p.monthly;
            const isCurrent = subscription?.plan === p.id;
            const dark = accent.dark;
            const displayPrice = interval === "year" ? priceObj.monthly_equivalent_pln : priceObj.price_pln;
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative rounded-3xl border-2 ${accent.ring} ${accent.bg} p-6 flex flex-col`}
              >
                {accent.badge && (
                  <div className={`absolute -top-3 left-6 text-[10px] font-bold px-2.5 py-1 rounded-full ${
                    p.id === "premium" ? "bg-[#FFD600] text-slate-900" : "bg-white text-slate-900"
                  }`}>
                    {accent.badge}
                  </div>
                )}
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-4 ${
                  dark ? "bg-[#FFD600] text-slate-900" : "bg-white border border-slate-200 text-slate-900"
                }`}>
                  <Icon className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <div className={`text-xs font-bold uppercase tracking-wider ${dark ? "text-yellow-300" : "text-slate-500"}`}>
                  Plan
                </div>
                <div className={`mt-1 text-2xl font-extrabold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>
                  {p.name}
                </div>
                <div className={`mt-4 flex items-baseline gap-1 ${dark ? "text-white" : "text-slate-900"}`}>
                  <span
                    className="text-4xl font-extrabold tracking-tight"
                    data-testid={`plan-price-${p.id}`}
                  >
                    {fmtPLN(displayPrice)}
                  </span>
                  <span className={`text-sm font-semibold ${dark ? "text-slate-300" : "text-slate-500"}`}>
                    zł / m-c
                  </span>
                </div>
                {interval === "year" && (
                  <div className={`text-xs mt-1 ${dark ? "text-yellow-300" : "text-slate-500"}`}>
                    Płacone rocznie ({fmtPLN(p.yearly.price_pln)} zł/rok) · oszczędzasz 20%
                  </div>
                )}
                <div className={`mt-3 text-sm font-semibold ${dark ? "text-yellow-300" : "text-slate-700"}`}>
                  {p.quota} quizów miesięcznie
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex gap-2 text-sm items-start">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${dark ? "text-yellow-300" : "text-green-600"}`} />
                      <span className={dark ? "text-slate-100" : "text-slate-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`choose-plan-${p.id}`}
                  disabled={isCurrent || loadingKey === priceObj.lookup_key}
                  onClick={() => checkout(priceObj.lookup_key)}
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-bold transition-colors ${
                    isCurrent
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : dark
                      ? "bg-[#FFD600] text-slate-900 hover:bg-yellow-300"
                      : p.id === "premium"
                      ? "bg-slate-900 text-white hover:bg-slate-800"
                      : "bg-[#FFD600] text-slate-900 hover:bg-[#E6C200]"
                  }`}
                >
                  {loadingKey === priceObj.lookup_key ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Otwieram...</>
                  ) : isCurrent ? (
                    <>Twój aktualny plan</>
                  ) : (
                    <>Wybierz {p.name} <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-slate-400">
          Płatność bezpieczna przez Stripe · Kod promocyjny można wpisać przy checkoucie · Możesz anulować w każdej chwili · Ceny brutto w PLN
        </p>
      </main>
    </div>
  );
}
